'use client';

import { useTranslation } from '@/hooks/useTranslation';
import { BiDollar, BiRefresh, BiRocket } from 'react-icons/bi';
import { BsGear } from 'react-icons/bs';
import { HeroBots, IImageOrientation } from '@/components/blocks/HeroBots';
import WhyChooseThisBot, { ColorDefaultChoice } from '@/components/blocks/WhyChooseThisBot';
import SiteFrequentlyAskedQuestionsComponent from '@/components/frequently-asked-questions/SiteFrequentlyAskedQuestionsComponent';
import React from 'react';
import { CtaTargetType } from '@/components/buttons/LouiseButton';
import CTABanner from '@/components/blocks/CTABanner';
import SiteScanHowItWorks from '@/components/blocks/SiteScanHowItWorks';
import { AnimatedGradientWrapper } from '@/components/background/AnimatedGradientWrapper';
import { FaUmbrellaBeach } from 'react-icons/fa';
import { FaGears } from 'react-icons/fa6';
import GeneralChatbotBenefits from '@/components/blocks/GeneralChatbotBenefits';
import { LiaShippingFastSolid } from 'react-icons/lia';

interface PageContentProps {
  locale: string;
}

export default function PageContent({ locale }: PageContentProps) {
  const { t } = useTranslation(locale);

  const hero = {
    content: {
      badge: t('PlugAndPlay/banner/badge'),
      title: t('PlugAndPlay/banner/title'),
      description: t('PlugAndPlay/banner/description'),
      cta: t('PlugAndPlay/banner/cta'),
      img: 'https://cdn.1cdn.app/application/LOUISEBOT/25072315340977_sitebot_hero.png',
      icon: BiRefresh,
      imgOrientation: 'portrait' as IImageOrientation,
      ctaTarget: 'signup' as CtaTargetType,
    },
    benefits: [
      { icon: <FaGears className="tw-w-5 tw-h-5" />, text: t('PlugAndPlay/banner/benefits/benefit1') },
      { icon: <BiDollar className="tw-w-5 tw-h-5" />, text: t('PlugAndPlay/banner/benefits/benefit2') },
      { icon: <FaUmbrellaBeach className="tw-w-5 tw-h-5" />, text: t('PlugAndPlay/banner/benefits/benefit3') },
    ],
    stats: [
      { value: '5 min', label: t('PlugAndPlay/banner/stats/stats1') },
      { value: 'Zero', label: t('PlugAndPlay/banner/stats/stats3') },
    ],
  };

  const sections = t('PlugAndPlay/whyChoose/sections', { returnObjects: true }) as Array<any>;

  const whyChooseThisBot = [
    {
      iconicText: sections[0].iconicText,
      title: sections[0].title,
      description: sections[0].description,
      icon: <LiaShippingFastSolid className="tw-w-10 tw-h-10 tw-text-white" />,
      outlineIcon: <LiaShippingFastSolid className="tw-w-6 tw-h-6 tw-text-purple-600" />,
      metricLabel: sections[0].metricLabel,
      button: sections[0].button,
      metric: sections[0].metric,
      colorFrom: 'pink' as ColorDefaultChoice,
      colorTo: 'purple' as ColorDefaultChoice,
    },
    {
      iconicText: sections[1].iconicText,
      title: sections[1].title,
      description: sections[1].description,
      icon: <BsGear className="tw-w-10 tw-h-10 tw-text-white" />,
      outlineIcon: <BsGear className="tw-w-6 tw-h-6 tw-text-blue-600" />,
      metricLabel: sections[1].metricLabel,
      button: sections[1].button,
      metric: sections[1].metric,
      colorFrom: 'blue' as ColorDefaultChoice,
      colorTo: 'cyan' as ColorDefaultChoice,
    },
    {
      iconicText: sections[2].iconicText,
      title: sections[2].title,
      description: sections[2].description,
      icon: <BiRocket className="tw-w-10 tw-h-10 tw-text-white" />,
      outlineIcon: <BiRocket className="tw-w-6 tw-h-6 tw-text-green-600" />,
      metricLabel: sections[2].metricLabel,
      button: sections[2].button,
      metric: sections[2].metric,
      colorFrom: 'green' as ColorDefaultChoice,
      colorTo: 'emerald' as ColorDefaultChoice,
    },
  ];

  return (
    <div>
      {/* Hero Section - Full Width */}
      <HeroBots benefits={hero.benefits} stats={hero.stats} content={hero.content} locale={locale} />

      <AnimatedGradientWrapper>
        <section className="tw-relative tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
          <WhyChooseThisBot
            title={t('PlugAndPlay/whyChoose/title')}
            subtitle={t('PlugAndPlay/whyChoose/subtitle')}
            description={t('PlugAndPlay/whyChoose/description')}
            sections={whyChooseThisBot}
          />
        </section>

        <SiteScanHowItWorks locale={locale} />

        <GeneralChatbotBenefits locale={locale} />

        <CTABanner
          title={t('Home/ReadyToGetStarted/cta-title')}
          description={t('Home/ReadyToGetStarted/cta-description')}
          cta={t('Home/ReadyToGetStarted/start-trial-aria')}
          ctaTarget="register"
          locale={locale}
        />
        <div className="tw-mt-10">
          <SiteFrequentlyAskedQuestionsComponent session="SiteBotFAQs" locale={locale} />
        </div>
      </AnimatedGradientWrapper>
    </div>
  );
}
